import { useEffect, useMemo, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarRange,
  ChartNoAxesCombined,
  CircleDollarSign,
  Package,
  ReceiptText,
  TrendingDown,
  Users,
} from "lucide-react";

import Loading from "../components/common/LoaderCommon";
import RevenueChart from "../components/Chart";
import {
  useGetStatistics,
  useGetTopSellingProducts,
} from "../service/dashboardService";
import { ProductResponse } from "../types/dashboardTypes";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../utils/components/ui/tabs";
import { useNavigate } from "react-router-dom";

type GraphInterval = "WEEKLY" | "MONTHLY" | "YEARLY";

function formatCurrency(value?: number | null) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "OMR",
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(Number(value || 0));
}

function formatCount(value?: number | null) {
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(Number(value || 0));
}

function formatInteger(value?: number | null) {
  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(Number(value || 0));
}

function MetricCard({
  title,
  value,
  subtitle,
  icon,
  accent = "blue",
}: {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  accent?: "blue" | "green" | "amber" | "slate";
}) {
  const accentStyles = {
    blue: "bg-[#EEF4FF] text-[#1A5EF9]",
    green: "bg-[#EAF8F1] text-[#1E9E61]",
    amber: "bg-[#FFF4E8] text-[#E08A1E]",
    slate: "bg-[#EEF2F8] text-[#51627E]",
  } as const;

  return (
    <div className="rounded-[22px] border border-[#E8EEF9] bg-white p-5 shadow-[0_18px_44px_rgba(26,94,249,0.08)]">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-[#7B8797]">{title}</p>
          <p className="mt-3 text-[28px] font-semibold tracking-[-0.03em] text-[#1B2B48]">
            {value}
          </p>
          <p className="mt-2 text-sm text-[#8D98A7]">{subtitle}</p>
        </div>
        <div className={`rounded-[16px] p-3 ${accentStyles[accent]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function ProductInsightList({
  items,
  tone,
  emptyMessage,
}: {
  items?: ProductResponse[];
  tone: "best" | "least" | "critical";
  emptyMessage: string;
}) {
  const toneStyles = {
    best: "bg-[#EAF8F1] text-[#1E9E61]",
    least: "bg-[#FFF4E8] text-[#E08A1E]",
    critical: "bg-[#FDEEEE] text-[#D14343]",
  } as const;

  if (!items?.length) {
    return (
      <div className="rounded-[18px] border border-dashed border-[#D8E4F8] bg-[#F9FBFF] px-4 py-10 text-center text-sm text-[#8D98A7]">
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item, idx) => (
        <div
          key={`${item.productName}-${idx}`}
          className="flex items-center justify-between gap-3 rounded-[18px] border border-[#ECF1FA] bg-[#FCFDFF] px-4 py-3 transition hover:border-[#D5E2FB] hover:bg-white"
        >
          <div className="flex min-w-0 items-center gap-3">
            <img
              src={item.imageUrl || "https://placehold.co/48x48/png"}
              alt={item.productName}
              className="h-11 w-11 rounded-[14px] object-cover ring-1 ring-[#DFE8F8]"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[#22324D]">
                {item.productName}
              </p>
              {/* <p className="truncate text-xs text-[#7F8B9B]">
                {item.brandName || "No brand"}
              </p> */}
            </div>
          </div>

          <div className="text-right">
            <span
              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${toneStyles[tone]}`}
            >
              {tone === "best"
                ? "Top seller"
                : tone === "least"
                  ? "Slow moving"
                  : "Low stock"}
            </span>
            <p className="mt-2 text-sm font-semibold text-[#22324D]">
              {formatCurrency(item.price)}
            </p>
            <p className="text-xs text-[#8D98A7]">
              Qty{" "}
              {tone === "critical" && item.displayQuantity
                ? item.displayQuantity
                : formatCount(
                    item.availableQuantity ?? item.totalQuantity ?? 0,
                  )}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}

const Profile = () => {
  const [graphTab, setGraphTab] = useState<GraphInterval>("WEEKLY");
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const navigate = useNavigate();
  const role = localStorage.getItem("role");
  useEffect(() => {
    if (role !== "ADMIN") {
      navigate("/accounting-list");
    }
  }, [role]);
  const today = new Date().toISOString().slice(0, 10);
  const startDate = dateRange.start || today;
  const endDate = dateRange.end || today;

  const statisticsRequest = {
    startDate,
    endDate,
    interval: "DAILY",
  };

  const { data: statistics, isLoading: statisticsLoading } =
    useGetStatistics(statisticsRequest);
  const { data: topSellingProducts, isLoading: topSellingProductsLoading } =
    useGetTopSellingProducts({
      pageNumber: 0,
      pageSize: 10,
      limit: 0,
    });

  const dateLabel = useMemo(() => {
    if (!dateRange.start && !dateRange.end) {
      return "Today";
    }

    return `${startDate} to ${endDate}`;
  }, [dateRange.end, dateRange.start, endDate, startDate]);

  const metricCards = [
    {
      title: "Products Sold",
      value: formatCount(statistics?.numberOfProductSold),
      subtitle: "Units sold in the selected date range",
      icon: <Package className="size-5" />,
      accent: "blue" as const,
    },
    {
      title: "Total Revenue",
      value: formatCurrency(statistics?.totalAmount),
      subtitle: "Gross amount generated from sales",
      icon: <CircleDollarSign className="size-5" />,
      accent: "green" as const,
    },
    {
      title: "Amount Received",
      value: formatCurrency(statistics?.amountReceived),
      subtitle: "Payments collected from customers",
      icon: <ArrowUpRight className="size-5" />,
      accent: "blue" as const,
    },
    {
      title: "Balance Amount",
      value: formatCurrency(statistics?.balanceAmount),
      subtitle: "Pending amount still to be collected",
      icon: <TrendingDown className="size-5" />,
      accent: "amber" as const,
    },
    {
      title: "Total Customers",
      value: formatInteger(statistics?.totalCustomers),
      subtitle: "Customers active in this reporting window",
      icon: <Users className="size-5" />,
      accent: "slate" as const,
    },
    {
      title: "New Customers",
      value: formatInteger(statistics?.newCustomers),
      subtitle: "Customers added during the selected period",
      icon: <ReceiptText className="size-5" />,
      accent: "green" as const,
    },
    {
      title: "Total Receivables",
      value: formatCurrency(statistics?.totalReceivables),
      subtitle: "Amount still owed by customers",
      icon: <ArrowDownRight className="size-5" />,
      accent: "amber" as const,
    },
    {
      title: "Total Payables",
      value: formatCurrency(statistics?.totalPayables),
      subtitle: "Amount still owed to vendors",
      icon: <ArrowUpRight className="size-5" />,
      accent: "slate" as const,
    },
  ];

  return (
    <div className="min-h-full bg-[#F7FAFF] py-1">
      <div className="space-y-6">
        <section className="overflow-hidden rounded-[28px] border border-[#E6EEFB] bg-[linear-gradient(135deg,#0F4FD6_0%,#1A5EF9_50%,#3A87FF_100%)] px-6 py-6 text-white shadow-[0_24px_60px_rgba(20,79,214,0.22)] md:px-8">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur">
                <ChartNoAxesCombined className="size-4" />
                Performance dashboard
              </div>
              <h1 className="mt-4 text-[24px] font-semibold tracking-[-0.03em]">
                Sales overview with stronger visibility into revenue, customers,
                and stock movement
              </h1>
              {/* <p className="mt-3 max-w-2xl text-sm leading-6 text-white/80">
                Use this dashboard to monitor financial performance, compare
                selling patterns, and quickly spot slow-moving or low-stock
                products.
              </p> */}
            </div>

            {/* <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-[20px] border border-white/18 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-[0.18em] text-white/65">
                  Active Range
                </p>
                <p className="mt-2 text-lg font-semibold">{dateLabel}</p>
                <p className="mt-1 text-sm text-white/75">
                  Applied to the KPI section below
                </p>
              </div>
              <div className="rounded-[20px] border border-white/18 bg-white/10 p-4 backdrop-blur">
                <p className="text-xs uppercase tracking-[0.18em] text-white/65">
                  Reporting Mode
                </p>
                <p className="mt-2 text-lg font-semibold">{graphTab}</p>
                <p className="mt-1 text-sm text-white/75">
                  Drives the revenue comparison chart
                </p>
              </div>
            </div> */}
          </div>
        </section>

        <section className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-[#1F314D]">
                Dashboard Filters
              </h2>
              <p className="mt-1 text-sm text-[#7D8998]">
                Adjust the reporting window to refresh sales and customer
                metrics.
              </p>
            </div>

            <div className="flex flex-col gap-3 md:flex-row">
              <label className="flex min-w-[200px] flex-col gap-2 text-sm font-medium text-[#50627E]">
                Start date
                <div className="relative">
                  <CalendarRange className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8EA0BB]" />
                  <input
                    type="date"
                    name="start"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange((prev) => ({
                        ...prev,
                        start: e.target.value,
                      }))
                    }
                    className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  />
                </div>
              </label>

              <label className="flex min-w-[200px] flex-col gap-2 text-sm font-medium text-[#50627E]">
                End date
                <div className="relative">
                  <CalendarRange className="pointer-events-none absolute left-4 top-1/2 size-4 -translate-y-1/2 text-[#8EA0BB]" />
                  <input
                    type="date"
                    name="end"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange((prev) => ({
                        ...prev,
                        end: e.target.value,
                      }))
                    }
                    className="h-12 w-full rounded-[14px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                  />
                </div>
              </label>
            </div>
          </div>
        </section>

        {statisticsLoading ? (
          <div className="flex min-h-[220px] items-center justify-center rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <Loading />
          </div>
        ) : (
          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {metricCards.map((card) => (
              <MetricCard
                key={card.title}
                title={card.title}
                value={card.value}
                subtitle={card.subtitle}
                icon={card.icon}
                accent={card.accent}
              />
            ))}
          </section>
        )}

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(360px,1fr)]">
          <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#21314A]">
                  Revenue Trends
                </h2>
                <p className="mt-1 text-sm text-[#7D8998]">
                  Compare revenue, profit, and cash flow over time.
                </p>
              </div>

              <Tabs
                defaultValue="WEEKLY"
                onValueChange={(value) => setGraphTab(value as GraphInterval)}
              >
                <TabsList className="h-auto rounded-[14px] border border-[#E6ECF8] bg-[#F5F8FD] p-1">
                  <TabsTrigger
                    value="WEEKLY"
                    className="rounded-[10px] px-4 py-2 text-sm font-medium text-[#5D6E8A] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white"
                  >
                    Weekly
                  </TabsTrigger>
                  <TabsTrigger
                    value="MONTHLY"
                    className="rounded-[10px] px-4 py-2 text-sm font-medium text-[#5D6E8A] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white"
                  >
                    Monthly
                  </TabsTrigger>
                  <TabsTrigger
                    value="YEARLY"
                    className="rounded-[10px] px-4 py-2 text-sm font-medium text-[#5D6E8A] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white"
                  >
                    Yearly
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="mt-6 rounded-[22px] border border-[#EEF2FA] bg-[linear-gradient(180deg,#FBFDFF_0%,#F6F9FF_100%)] p-4 sm:p-6">
              <div className="overflow-x-auto">
                <div className="min-w-[560px]">
                  <RevenueChart interval={graphTab} />
                </div>
              </div>
            </div>
          </div>

          <div className="rounded-[26px] border border-[#E8EEF9] bg-white p-6 shadow-[0_16px_40px_rgba(26,94,249,0.08)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-[#21314A]">
                  Product Insights
                </h2>
                <p className="mt-1 text-sm text-[#7D8998]">
                  Track top performers, slow movers, and stock risks from one
                  panel.
                </p>
              </div>
              <div className="rounded-full bg-[#EEF4FF] px-3 py-1 text-xs font-semibold text-[#1A5EF9]">
                Live snapshot
              </div>
            </div>

            <Tabs defaultValue="best" className="mt-6">
              <TabsList className="grid h-auto w-full grid-cols-3 rounded-[14px] border border-[#E6ECF8] bg-[#F5F8FD] p-1">
                <TabsTrigger
                  value="best"
                  className="rounded-[10px] px-3 py-2 text-sm font-medium text-[#5D6E8A] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white"
                >
                  Best
                </TabsTrigger>
                <TabsTrigger
                  value="least"
                  className="rounded-[10px] px-3 py-2 text-sm font-medium text-[#5D6E8A] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white"
                >
                  Least
                </TabsTrigger>
                <TabsTrigger
                  value="critical"
                  className="rounded-[10px] px-3 py-2 text-sm font-medium text-[#5D6E8A] data-[state=active]:bg-[#1A5EF9] data-[state=active]:text-white"
                >
                  Critical
                </TabsTrigger>
              </TabsList>

              <div className="mt-5 max-h-[520px] overflow-y-auto pr-1">
                <TabsContent value="best" className="mt-0">
                  {topSellingProductsLoading ? (
                    <div className="flex min-h-[220px] items-center justify-center">
                      <Loading />
                    </div>
                  ) : (
                    <ProductInsightList
                      items={topSellingProducts?.bestSellingProductResponseList}
                      tone="best"
                      emptyMessage="No best-selling product data is available yet."
                    />
                  )}
                </TabsContent>

                <TabsContent value="least" className="mt-0">
                  {topSellingProductsLoading ? (
                    <div className="flex min-h-[220px] items-center justify-center">
                      <Loading />
                    </div>
                  ) : (
                    <ProductInsightList
                      items={
                        topSellingProducts?.leastSellingProductResponseList
                      }
                      tone="least"
                      emptyMessage="No least-selling product data is available yet."
                    />
                  )}
                </TabsContent>

                <TabsContent value="critical" className="mt-0">
                  {topSellingProductsLoading ? (
                    <div className="flex min-h-[220px] items-center justify-center">
                      <Loading />
                    </div>
                  ) : (
                    <ProductInsightList
                      items={topSellingProducts?.productWithLowQuantity}
                      tone="critical"
                      emptyMessage="No critical stock items are available yet."
                    />
                  )}
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </section>

        {/* <section className="grid gap-4 lg:grid-cols-3">
          <div className="rounded-[22px] border border-[#E8EEF9] bg-white p-5 shadow-[0_14px_36px_rgba(26,94,249,0.08)]">
            <div className="flex items-center gap-3">
              <div className="rounded-[14px] bg-[#EEF4FF] p-3 text-[#1A5EF9]">
                <CircleDollarSign className="size-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#21314A]">
                  Collection Health
                </h3>
                <p className="text-sm text-[#7D8998]">
                  Amount received versus outstanding balance
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-end justify-between">
              <div>
                <p className="text-sm text-[#7D8998]">Received</p>
                <p className="mt-2 text-2xl font-semibold text-[#1F314D]">
                  {formatCurrency(statistics?.amountReceived)}
                </p>
              </div>
              <div className="rounded-full bg-[#EAF8F1] px-3 py-1 text-xs font-semibold text-[#1E9E61]">
                Healthy inflow
              </div>
            </div>
          </div>

          <div className="rounded-[22px] border border-[#E8EEF9] bg-white p-5 shadow-[0_14px_36px_rgba(26,94,249,0.08)]">
            <div className="flex items-center gap-3">
              <div className="rounded-[14px] bg-[#FFF4E8] p-3 text-[#E08A1E]">
                <TrendingDown className="size-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#21314A]">
                  Outstanding Risk
                </h3>
                <p className="text-sm text-[#7D8998]">
                  Keep a close watch on pending receivables
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-end justify-between">
              <div>
                <p className="text-sm text-[#7D8998]">Pending balance</p>
                <p className="mt-2 text-2xl font-semibold text-[#1F314D]">
                  {formatCurrency(statistics?.balanceAmount)}
                </p>
              </div>
              <div className="rounded-full bg-[#FFF4E8] px-3 py-1 text-xs font-semibold text-[#E08A1E]">
                Needs follow-up
              </div>
            </div>
          </div>

          <div className="rounded-[22px] border border-[#E8EEF9] bg-white p-5 shadow-[0_14px_36px_rgba(26,94,249,0.08)]">
            <div className="flex items-center gap-3">
              <div className="rounded-[14px] bg-[#EEF2F8] p-3 text-[#51627E]">
                <Users className="size-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-[#21314A]">
                  Customer Growth
                </h3>
                <p className="text-sm text-[#7D8998]">
                  New customer acquisition within the active range
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-end justify-between">
              <div>
                <p className="text-sm text-[#7D8998]">New customers</p>
                <p className="mt-2 text-2xl font-semibold text-[#1F314D]">
                  {formatCount(statistics?.newCustomers)}
                </p>
              </div>
              <div className="rounded-full bg-[#EEF4FF] px-3 py-1 text-xs font-semibold text-[#1A5EF9]">
                Growth watch
              </div>
            </div>
          </div>
        </section> */}
      </div>
    </div>
  );
};

export default Profile;
